import React from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import ChevronRight from '@material-ui/icons/ChevronRight';
import { Button, Typography, makeStyles } from '@material-ui/core';
import { Input } from 'components/FormInputs';
import { useFormik } from 'components/shared/Formik';
import CircularProgress from 'components/shared/CircularProgress';

const styles = makeStyles((theme) => ({
  button: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  ellipsis: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  input: {
    '& label': {
      [theme.breakpoints.down('xs')]: {
        top: '-5px',
      },
    },
  },
}));

const LoginStepTwo = ({
  onSubmit,
  resetPassword,
  userType,
  username,
  error,
  loading,
}) => {
  const classes = styles();
  const intl = useIntl();
  const formik = useFormik({
    initialValues: {
      password: '',
    },
    validate: (values) => {
      const errors = {};

      if (!values.password) {
        errors.password = 'Vänligen fyll i ditt lösenord';
      }

      return errors;
    },
    validateOnBlur: true,
    validateOnChange: true,
    enableReinitialize: true,
    onSubmit: ({ password }) => {
      onSubmit({ password, username });
    },
  });

  const {
    handleChange, handleSubmit, values, errors, isValid, setErrors, handleBlur,
  } = formik;

  React.useEffect(() => {
    if (error && error.message === 'Password does not match') {
      setErrors({
        password: 'Fel lösenord, försök igen',
      });
    }
  }, [error]);

  if (!userType) {
    // new user
    return (
      <div>
        <Typography variant="h6" paragraph className={classes.ellipsis}>
          <FormattedMessage
            id="Welcome"
            defaultMessage="Welcome"
          />{' '}{username}
        </Typography>
        <Typography>
          <FormattedMessage
            id="welcome.cantFindAccount"
            defaultMessage="We can't find any account connected to this email address,
              click on Continue to create an account."
          />
        </Typography>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          className={classes.button}
          onClick={() => onSubmit({ username })}
        >
          <FormattedMessage
            id="welcome.createAccount"
            defaultMessage="Create Account"
          />
          <ChevronRight />
        </Button>
      </div>
    );
  }

  return (
    <div>
      <Typography variant="h6" paragraph className={classes.ellipsis}>
        <FormattedMessage
          id="welcome.welcomeBack"
          defaultMessage="Welcome back"
        />
        {' '}{username}
      </Typography>

      <form onSubmit={handleSubmit} noValidate>
        <Input
          label={intl.formatMessage({
            id: 'welcome.fillInPass',
            defaultMessage: 'Fill in your password to login',
          })}
          placeholder={intl.formatMessage({
            id: 'Password',
            defaultMessage: 'Password',
          })}
          name="password"
          type="password"
          InputLabelProps={{
            shrink: true,
          }}
          autoFocus
          margin="normal"
          value={values.password}
          errors={errors}
          onChange={handleChange}
          onBlur={handleBlur}
          className={classes.input}
          disabled={loading}
        />
        {
          loading && <CircularProgress />
        }
        <Button
          fullWidth
          variant="contained"
          color="primary"
          className={classes.button}
          type="submit"
          disabled={!isValid || loading}
        >
          <FormattedMessage
            id="welcome.signIn"
            defaultMessage="Sign in"
          />
          <ChevronRight />
        </Button>
      </form>
      <Button
        fullWidth
        onClick={() => resetPassword({ username })}
        disabled={loading}
      >
        <FormattedMessage
          id="welcome.forgotPassword"
          defaultMessage="Forgot your password?"
        />
      </Button>
    </div>
  );
};

LoginStepTwo.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  resetPassword: PropTypes.func.isRequired,
  userType: PropTypes.bool.isRequired,
  username: PropTypes.string.isRequired,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.shape({ message: PropTypes.string })]),
  loading: PropTypes.bool,
};

export default LoginStepTwo;
